import React, { useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import { defineMessages } from 'react-intl';
import BannerDividerIcon from '../../../Icons/BannerDivider';
import Button from '../../../Button';
import * as constants from '../../../constants';
import styles from './PromoBanner.module.css';
import Text from '../../../Text';
import CountdownTimer from './Countdown/CountdownTimer/CountdownTimer';
import {
  DEFAULT_COUNTDOWN_WINDOW_HOURS,
  getBannerHref,
  getCountdownSeconds,
  stripAnchorTags,
} from './helpers';

const SITEWIDE_BANNER = 'sitewide_banner';

const endsInMessage = defineMessages({
  endsIn: {
    id: 'react.headerAndFooter.components.PromoBanner.endsIn',
    defaultMessage: 'ends in:',
  },
  endsSoon: {
    id: 'react.headerAndFooter.components.PromoBanner.endsSoon',
    defaultMessage: '{message} ends soon',
  },
});

const PromoBanner = ({
  banners,
  intl,
  coralSaleBannerWithShopNowAndInlineCountdown,
  countdownWindowHours,
  shopNowLabel,
  countdownEndsInLabel,
}: any) => {
  const [inViewRef, inView] = useInView({ triggerOnce: true });
  const containerRef = useRef<any>(null);
  const [banner] = banners || [];
  const {
    message = '',
    showCountdown = false,
    secondsRemaining = 0,
    promotionName,
  } = banner || {};
  const isCoralSale = coralSaleBannerWithShopNowAndInlineCountdown;
  const countdownSeconds = getCountdownSeconds(secondsRemaining, countdownWindowHours);
  const bannerMessageTrimmed = isCoralSale
    ? stripAnchorTags(message)
    : message;
  const showTimer = showCountdown && countdownSeconds > 0;

  const ctaHref = getBannerHref(banner, message);
  const ctaLabel = (banner && banner.ctaLabel) || shopNowLabel;

  const combinedRefs = useCallback(
    (node?: any) => {
      containerRef.current = node;
      inViewRef(node);
    },
    [inView],
  );

  React.useEffect(() => {
    if (inView && message) {
      (window as any).dataLayer = (window as any).dataLayer || [];

      (window as any).dataLayer.push({
        event: 'view_promotion',
        page_location: (window as any).location.href,
        ecommerce: {
          promotion_name: promotionName,
          creative_slot: SITEWIDE_BANNER,
        },
      });
    }
  }, [inView]);

  const logEvent = () => {
    try {
      (window as any).dataLayer.push({
        event: 'select_promotion',
        page_location: (window as any).location.href,
        ecommerce: {
          promotion_name: promotionName,
          creative_slot: SITEWIDE_BANNER,
        },
      });
    } catch (err) {
      console.error('There was error reporting select_promotion event', err);
    }
  };

  React.useEffect(() => {
    const container = containerRef.current;
    const handleClick = (event?: any) => {
      const link = event.target.closest('a');
      if (link && container && container.contains(link)) {
        logEvent();
      }
    };

    if (message && container) {
      container.addEventListener('click', handleClick);
    }

    return () => {
      if (message && container) {
        container.removeEventListener('click', handleClick);
      }
    };
  }, [message]);

  if (!banners.length) {
    return null;
  }

  const coralBannerMessage = showCountdown && secondsRemaining <= 0
    ? intl.formatMessage(endsInMessage.endsSoon, { message: bannerMessageTrimmed })
    : bannerMessageTrimmed;

  const legacyMessage = showCountdown && countdownSeconds <= 0
    ? intl.formatMessage(endsInMessage.endsSoon, { message: bannerMessageTrimmed })
    : bannerMessageTrimmed;

  if (isCoralSale) {
    return (
      <div
        className={cnames(styles.promoBannerContainer, styles.coralSaleBanner)}
        ref={combinedRefs}
        data-testid="ds-coral-sale-banner"
      >
        <span className={styles.bannerDivider}>
          <BannerDividerIcon size={constants.MEDIUM} color={constants.COLOR_WHITE} ariaHidden />
        </span>
        <div className={styles.saleContent}>
          <span className={styles.saleMessage} dangerouslySetInnerHTML={{ __html: coralBannerMessage }} />
          {showTimer && (
            <span className={styles.saleTimerPill} data-testid="ds-sale-banner-timer">
              <CountdownTimer
                initialSeconds={countdownSeconds}
                format="inline"
                intl={intl}
                endsInLabel={countdownEndsInLabel}
              />
            </span>
          )}
        </div>
        {ctaHref && (
          <Button size={constants.SMALL} elevation={constants.ELEVATION_MEDIUM} href={ctaHref}>
            {ctaLabel}
          </Button>
        )}
        <span className={styles.bannerDivider}>
          <BannerDividerIcon size={constants.MEDIUM} color={constants.COLOR_WHITE} ariaHidden />
        </span>
      </div>
    );
  }

  return (
    <div
      className={cnames(styles.promoBannerContainer, {
        [styles.showCountdown]: countdownSeconds > 0,
        [styles.colourfulBanner]: !countdownSeconds,
      })}
      ref={combinedRefs}
    >
      <span className={styles.countdownBannerContainer}>
        <span className={styles.countdownDividerLeft}>
          <BannerDividerIcon color="#E7F8EF" />
        </span>
        <span className={cnames(styles.bannerMessage, { [styles.countdownText]: showCountdown })}>
          <Text type="body2">
            <span role="button" tabIndex={0} dangerouslySetInnerHTML={{ __html: legacyMessage }} />
          </Text>
        </span>
        {showTimer && (
          <span className={styles.countdownTimerContainer}>
            <span className={styles.countdownText}>
              <Text type="body2">
                {intl.formatMessage(endsInMessage.endsIn)}
              </Text>
            </span>
            <CountdownTimer initialSeconds={countdownSeconds} />
          </span>
        )}
        <span className={styles.countdownDividerRight}>
          <BannerDividerIcon color="#E7F8EF" />
        </span>
      </span>
    </div>
  );
};

(PromoBanner as any).propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
      showCountdown: PropTypes.bool,
      secondsRemaining: PropTypes.number,
      promotionName: PropTypes.string,
      url: PropTypes.string,
      href: PropTypes.string,
      link: PropTypes.string,
      ctaLabel: PropTypes.string,
    }),
  ),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  coralSaleBannerWithShopNowAndInlineCountdown: PropTypes.bool,
  countdownWindowHours: PropTypes.number,
  shopNowLabel: PropTypes.string,
  countdownEndsInLabel: PropTypes.string,
};

(PromoBanner as any).defaultProps = {
  banners: [],
  intl: undefined,
  coralSaleBannerWithShopNowAndInlineCountdown: false,
  countdownWindowHours: DEFAULT_COUNTDOWN_WINDOW_HOURS,
  shopNowLabel: undefined,
  countdownEndsInLabel: undefined,
};

export default PromoBanner;
